import { useContext, useEffect, useCallback, useRef } from 'react';
import { UNSAFE_NavigationContext } from 'react-router';
import { confirmDialog } from 'primereact/confirmdialog';

export const useUnsavedChanges = (
    isDirty: boolean,
    customMessage?: string,
    rejectBtnText?: string,
    acceptBtnText?: string,
) => {
    const { navigator } = useContext(UNSAFE_NavigationContext);
    const isDirtyRef = useRef(isDirty);
    isDirtyRef.current = isDirty;

    const message = customMessage || 'Есть несохранённые изменения. Данные будут потеряны. Продолжить?';
    const rejBtnText = rejectBtnText || 'Остаться';
    const actBtnText = acceptBtnText || 'Уйти';

    useEffect(() => {
        if (!isDirty) return;

        const originalPush = navigator.push;
        const originalReplace = navigator.replace;

        navigator.push = (...args: Parameters<typeof navigator.push>) => {
            confirmDialog({
                message,
                icon: 'fa-solid fa-triangle-exclamation',
                className: 'pd-dialog',
                acceptLabel: actBtnText,
                acceptIcon: 'fa-solid fa-arrow-right-from-bracket',
                rejectLabel: rejBtnText,
                rejectIcon: 'fa-solid fa-arrow-left',
                acceptClassName: 'pd-confirm-btn-accept',
                rejectClassName: 'pd-confirm-btn-reject',
                accept: () => {
                    navigator.push = originalPush;
                    navigator.replace = originalReplace;
                    originalPush(...args);
                },
            });
        };

        navigator.replace = (...args: Parameters<typeof navigator.replace>) => {
            confirmDialog({
                message,
                icon: 'fa-solid fa-triangle-exclamation',
                className: 'pd-dialog',
                acceptLabel: actBtnText,
                acceptIcon: 'fa-solid fa-arrow-right-from-bracket',
                rejectLabel: rejBtnText,
                rejectIcon: 'fa-solid fa-arrow-left',
                acceptClassName: 'pd-confirm-btn-accept',
                rejectClassName: 'pd-confirm-btn-reject',
                accept: () => {
                    navigator.push = originalPush;
                    navigator.replace = originalReplace;
                    originalReplace(...args);
                },
            });
        };

        return () => {
            navigator.push = originalPush;
            navigator.replace = originalReplace;
        };
    }, [isDirty, navigator]);

    useEffect(() => {
        const handler = (e: BeforeUnloadEvent) => {
            if (!isDirtyRef.current) return;
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handler);
        return () => window.removeEventListener('beforeunload', handler);
    }, []);

    const requestNavigate = useCallback((onProceed: () => void) => {
        if (isDirtyRef.current) {
            confirmDialog({
                message,
                icon: 'fa-solid fa-triangle-exclamation',
                className: 'pd-dialog',
                acceptLabel: actBtnText,
                acceptIcon: 'fa-solid fa-arrow-right-from-bracket',
                rejectLabel: rejBtnText,
                rejectIcon: 'fa-solid fa-arrow-left',
                acceptClassName: 'pd-confirm-btn-accept',
                rejectClassName: 'pd-confirm-btn-reject',
                accept: onProceed,
            });
        } else {
            onProceed();
        }
    }, []);

    return { requestNavigate };
};
